import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { IExploreFilters } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.types';
import { FilterCheckboxItem } from 'src/features/white-labels/ocean/explore/components/explore-nft-filter-items/filter-checkbox-item';
import { Typography } from 'src/ui/common/atoms/typography';
import { StyledMore, StyledNoData, StyledSearchInput } from './styles';

export type ItemT = {
    id: string;
    name: string;
};

interface IProps {
    data: ItemT[];
    filterName: keyof IExploreFilters;
    onSubmit: (e: React.ChangeEvent<HTMLInputElement>) => void;
    loadMore: () => void;
    onSearch: (value: string) => void;
    total: number;
    loading?: boolean;
    showSearch: boolean;
    selected: any[];
    onFilterChange: (key: keyof IExploreFilters, payload: any) => void;
}

export const FilterRender: React.VFC<IProps> = ({
    showSearch,
    filterName,
    onFilterChange,
    selected,
    onSubmit,
    loading,
    total,
    data,
    onSearch,
    loadMore,
}) => {
    const { t } = useTranslation(['actions', 'components']);
    const [searchValue, setSearchValue] = useState('');

    const locale = useMemo(
        () => ({
            showMore: t('misc.showMore', { ns: 'actions' }),
            searchPlaceholder: t('exploreFilter.filter.search.placeholder', { ns: 'components' }),
        }),
        [t]
    );

    const showMore = useMemo(() => data.length < total, [data.length, total]);

    const handleSearch = useCallback(
        (e) => {
            setSearchValue(e.target.value);
            onSearch(e.target.value);
        },
        [onSearch]
    );

    const handleFilterChange = useCallback(
        (id, value: boolean) => {
            const newSet = new Set(selected);
            if (!value) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            onFilterChange(filterName, [...newSet]);
        },
        [filterName, onFilterChange, selected]
    );

    const onClear = useCallback(() => {
        setSearchValue('');
        onSearch('');
    }, [onSearch]);

    return (
        <>
            {showSearch && (
                <StyledSearchInput
                    onSubmit={onSubmit}
                    onChange={handleSearch}
                    placeholder={locale.searchPlaceholder}
                    value={searchValue}
                    onClear={onClear}
                />
            )}
            {data.map((item) => (
                <FilterCheckboxItem
                    key={item.id}
                    id={item.id}
                    label={item.name}
                    onChange={handleFilterChange}
                    checked={selected?.includes(item.id)}
                />
            ))}
            {showMore && (
                <StyledMore disabled={loading} onClick={loadMore} variant="none" stretch>
                    {locale.showMore}
                </StyledMore>
            )}
            {showSearch && !loading && !data.length && (
                <StyledNoData flexDirection="column" alignItems="center" justifyContent="center">
                    <img src="/icons/no-data-cards.svg" alt="no data" />
                    <Typography>No items found</Typography>
                </StyledNoData>
            )}
        </>
    );
};
